import type { SchedulingRow } from "@/types/scheduling";
import type { ScopeFilters } from "@/types/filters";
import type { ComputedMetrics, AppointmentMixItem, AppointmentGroupItem } from "@/types/metrics";
import { EMPTY_METRICS } from "@/types/metrics";
import { getWeekKey, getWeeksInRange } from "@/lib/periods";

export const SLUG_TO_GROUP: Record<string, string> = {
  exam_wellness: "Exam",
  exam_new_pet: "Exam",
  exam_progress: "Exam",
  exam_injury_illness: "Exam",
  exam_wellness_exotic: "Exotic",
  exam_injury_illness_exotic: "Exotic",
  exam_urgent: "Urgent",
  exam_urgent_exotic: "Exotic",
  surgery_spay: "Spay & Neuter",
  surgery_neuter: "Spay & Neuter",
  surgery_orthopedic: "Surgery",
  surgery_soft_tissue: "Surgery",
  surgery_other: "Surgery",
  procedure: "Procedure",
  procedure_sedated: "Procedure",
  procedure_holistic: "Procedure",
  dental: "Dental",
  euthanasia: "Other",
  dead_on_arrival: "Other",
  house_call: "Other",
  boarding: "Other",
  grooming: "Other",
  technician_visit: "Other",
  exam_progress_exotic: "Exotic",
  telemedicine_general: "Other",
  telemedicine_progress: "Other",
  custom: "Other",
};

export const APPT_SLUGS = [
  "exam_wellness",
  "exam_new_pet",
  "exam_progress",
  "exam_injury_illness",
  "exam_wellness_exotic",
  "exam_injury_illness_exotic",
  "exam_urgent",
  "exam_urgent_exotic",
  "surgery_spay",
  "surgery_neuter",
  "surgery_orthopedic",
  "surgery_soft_tissue",
  "surgery_other",
  "procedure",
  "procedure_sedated",
  "procedure_holistic",
  "dental",
  "euthanasia",
  "dead_on_arrival",
  "house_call",
  "boarding",
  "grooming",
  "technician_visit",
  "exam_progress_exotic",
  "telemedicine_general",
  "telemedicine_progress",
  "custom",
] as const;

const APPT_KIND_KEYS = APPT_SLUGS.map(
  (slug) => [slug, `appointment_kind_${slug}_minutes` as keyof SchedulingRow] as const
);

export function applyBaseFilter(
  rows: SchedulingRow[],
  excludeZeroApptDays = false
): SchedulingRow[] {
  if (!excludeZeroApptDays) return rows;
  return rows.filter((r) => Number(r.completed_appointment_minutes) > 0);
}

export function filterRowsByDateRange(
  rows: SchedulingRow[],
  startDate: string,
  endDate: string
): SchedulingRow[] {
  return rows.filter((r) => r.schedule_date >= startDate && r.schedule_date <= endDate);
}

export function filterRowsByScope(rows: SchedulingRow[], scope: ScopeFilters): SchedulingRow[] {
  return rows.filter((r) => {
    if (scope.countryCode && r.country_code !== scope.countryCode) return false;
    if (scope.divisionName && r.division_name !== scope.divisionName) return false;
    if (scope.groupName && r.group_name !== scope.groupName) return false;
    if (scope.locationId && r.location_id !== scope.locationId) return false;
    if (scope.resourceId && r.resource_id !== scope.resourceId) return false;
    return true;
  });
}

export function humanizeSlug(slug: string): string {
  return slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function sumKindMinutes(rows: SchedulingRow[]): Record<string, number> {
  const sums: Record<string, number> = {};
  for (const [slug] of APPT_KIND_KEYS) sums[slug] = 0;
  for (const row of rows) {
    for (const [slug, key] of APPT_KIND_KEYS) {
      sums[slug] = (sums[slug] ?? 0) + Number(row[key] ?? 0);
    }
  }
  return sums;
}

export function sumSelectedKindMinutes(rows: SchedulingRow[], slugs: string[]): number {
  const keys = slugs.map((s) => `appointment_kind_${s}_minutes` as keyof SchedulingRow);
  let total = 0;
  for (const row of rows) {
    for (const key of keys) total += Number(row[key] ?? 0);
  }
  return total;
}

function computeAppointmentMix(
  slugMinutes: Record<string, number>,
  selected: string[],
  totalMinutes: number
): AppointmentMixItem[] {
  if (totalMinutes <= 0) return [];

  const slugs =
    selected.length > 0 ? APPT_SLUGS.filter((s) => selected.includes(s)) : [...APPT_SLUGS];
  return slugs
    .map((slug) => ({
      group: slug as string,
      label: humanizeSlug(slug),
      minutes: slugMinutes[slug] ?? 0,
      pct: (slugMinutes[slug] ?? 0) / totalMinutes,
    }))
    .filter((item) => item.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

function computeAppointmentGroupMix(slugMix: AppointmentMixItem[]): AppointmentGroupItem[] {
  const groupMap = new Map<string, { minutes: number; types: AppointmentMixItem[] }>();
  for (const item of slugMix) {
    const groupName = SLUG_TO_GROUP[item.group] ?? "Other";
    if (!groupMap.has(groupName)) groupMap.set(groupName, { minutes: 0, types: [] });
    const g = groupMap.get(groupName)!;
    g.minutes += item.minutes;
    g.types.push(item);
  }
  const totalMinutes = slugMix.reduce((s, i) => s + i.minutes, 0);
  return Array.from(groupMap.entries())
    .map(([group, { minutes, types }]) => ({
      group,
      minutes,
      pct: totalMinutes > 0 ? minutes / totalMinutes : 0,
      types: types.sort((a, b) => b.minutes - a.minutes),
    }))
    .sort((a, b) => b.minutes - a.minutes);
}

// Every raw sum computeMetrics needs, whether produced by reducing rows
// client-side or by SQL sums server-side. deriveMetrics (below) is the single
// formula both paths share, so the two can never drift on arithmetic — only
// on how MetricTotals itself gets built.
export interface MetricTotals {
  dvmDays: number;
  distinctDvms: number;
  // Distinct (doctor, week) pairs. The denominator for per-DVM-per-week
  // metrics — see the comment on getWeekKey for why distinctDvms is wrong.
  dvmWeeks: number;
  distinctDates: number;
  // Two different denominators, deliberately both carried.
  //
  // totalScheduledMin is the roster: what the doctor was booked to work. It is
  // the only thing knowable about FUTURE capacity, so the Capacity tab divides
  // by it and nothing else.
  //
  // totalWorkedMin is what happened, including early starts and overruns. The
  // three activity buckets sum to it exactly, so Utilization divides by it --
  // against the roster they only balanced on 85% of rows.
  totalScheduledMin: number;
  totalWorkedMin: number;
  totalBlockoffMin: number;
  allSeenMin: number;
  /** Sourced, not derived. Never negative, unlike the subtraction it replaced. */
  totalAvailMin: number;
  slugMinutes: Record<string, number>;
  totalBookableBlocks: number;
  totalMultiBookedBlocks: number;
  totalCompleted: number;
  totalCanceled: number;
  // Revenue and the cancellation reason split. Present on MetricTotals rather
  // than on a second totals type because the Revenue and Cancellation tabs read
  // the SAME rollup rows as Utilization -- a parallel aggregation would be a
  // second chance to disagree about which rows survive a filter.
  totalMedRev: number;
  canceledByReason: Record<CancelReason, number>;
  /** Summed, not averaged: the mean is this over totalCanceled. */
  totalCancelLeadDays: number;
}

export const CANCEL_REASONS = [
  "no_show",
  "pending_reschedule",
  "pet_seen_elsewhere",
  "concern_resolved",
  "other",
  "unknown_reason",
] as const;

export type CancelReason = (typeof CANCEL_REASONS)[number];

export function emptyCancelReasons(): Record<CancelReason, number> {
  return {
    no_show: 0,
    pending_reschedule: 0,
    pet_seen_elsewhere: 0,
    concern_resolved: 0,
    other: 0,
    unknown_reason: 0,
  };
}

// All of computeMetrics's derived ratios/rates, given pre-aggregated totals.
// periodStart/periodEnd must be the SAME window the totals were aggregated
// over (current vs. current, priorYear vs. priorYear, etc.) — getWeeksInRange
// only knows the calendar, not which rows actually contributed.
export function deriveMetrics(
  totals: MetricTotals,
  periodStart: string,
  periodEnd: string,
  appointmentTypes: string[] = []
): ComputedMetrics {
  if (totals.dvmDays === 0) return EMPTY_METRICS;

  const typeFilterActive = appointmentTypes.length > 0;
  const { slugMinutes } = totals;

  const totalScheduledMin = totals.totalScheduledMin;
  const totalWorkedMin = totals.totalWorkedMin;
  const totalBlockoffMin = totals.totalBlockoffMin;
  const allSeenMin = totals.allSeenMin;
  // With a type filter, "seen" counts only the selected kinds; the rest of the
  // seen time is reported separately so seen + other + avail + blockoff = scheduled.
  const totalSeenMin = typeFilterActive
    ? appointmentTypes.reduce((s, slug) => s + (slugMinutes[slug] ?? 0), 0)
    : allSeenMin;
  const totalOtherTypesMin = typeFilterActive ? Math.max(0, allSeenMin - totalSeenMin) : 0;
  // Read from the source column, not subtracted. The old
  // scheduled - blockoff - seen took deduped wall-clock time away from a roster
  // figure and went negative on 284 rows in a sample week; the source never does.
  const totalAvailMin = totals.totalAvailMin;

  const dvmDays = totals.dvmDays;
  // Capacity hours stay on the roster -- see MetricTotals. The Utilization
  // tab's headline card uses workedHours instead.
  const dvmHours = totalScheduledMin / 60;
  const workedHours = totalWorkedMin / 60;
  const avgWorkedHoursPerDay = dvmDays > 0 ? workedHours / dvmDays : 0;

  // Elapsed weeks, not whole weeks: a 9-day MTD is 1.29 weeks, and dividing
  // nine days of hours by "one complete week" would overstate the weekly
  // average by a third. Trailing periods still land on exact integers.
  const completeWeeksWorked = getWeeksInRange(periodStart, periodEnd);
  const avgHoursPerDay = dvmDays > 0 ? dvmHours / dvmDays : 0;
  const avgHoursPerWeekTotal = completeWeeksWorked > 0 ? dvmHours / completeWeeksWorked : 0;

  const distinctDvms = totals.distinctDvms;
  const distinctDates = totals.distinctDates;
  const dvmsPerDay = distinctDates > 0 ? dvmDays / distinctDates : 0;
  // Divided by DVM-weeks, not weeks x doctors: a doctor who worked two of the
  // four weeks contributes two, not four. Identical to the old maths whenever
  // the roster is stable, and only diverges where the old maths was wrong.
  // The equation the Capacity cards display still holds exactly:
  //   (dvmHours/dvmDays) x (dvmDays/dvmWeeks) = dvmHours/dvmWeeks
  const dvmWeeks = totals.dvmWeeks;
  const daysPerDvmPerWeek = dvmWeeks > 0 ? dvmDays / dvmWeeks : 0;
  const avgHoursPerDvmPerWeek = dvmWeeks > 0 ? dvmHours / dvmWeeks : 0;

  // All four over WORKED minutes, and they have to move together: seen +
  // block-off + available sum to total_worked_minutes exactly, so mixing
  // denominators would leave the three-segment breakdown bar refusing to reach
  // 100%. Measured over a sample week, the three sum to 100.00% against worked
  // and 101.75% against the roster.
  const blockoffRate = totalWorkedMin > 0 ? totalBlockoffMin / totalWorkedMin : 0;
  const petSeenRate = totalWorkedMin > 0 ? totalSeenMin / totalWorkedMin : 0;
  const otherTypesRate = totalWorkedMin > 0 ? totalOtherTypesMin / totalWorkedMin : 0;
  const availableTimeRate = totalWorkedMin > 0 ? totalAvailMin / totalWorkedMin : 0;

  const multiBookedPct =
    totals.totalBookableBlocks > 0 ? totals.totalMultiBookedBlocks / totals.totalBookableBlocks : 0;

  const totalCompleted = totals.totalCompleted;
  const avgCompletedPerDay = dvmDays > 0 ? totalCompleted / dvmDays : 0;
  const totalCanceled = totals.totalCanceled;

  const avgBookingLeadTimeDays: number | null = null;

  const avgSeenHoursPerDvmPerDay = dvmDays > 0 ? totalSeenMin / 60 / dvmDays : 0;
  const avgOtherTypesHoursPerDvmPerDay = dvmDays > 0 ? totalOtherTypesMin / 60 / dvmDays : 0;
  const avgAvailHoursPerDvmPerDay = dvmDays > 0 ? totalAvailMin / 60 / dvmDays : 0;
  const avgBlockoffHoursPerDvmPerDay = dvmDays > 0 ? totalBlockoffMin / 60 / dvmDays : 0;
  const avgDvmDaysPerWeek = completeWeeksWorked > 0 ? dvmDays / completeWeeksWorked : 0;

  const appointmentMix = computeAppointmentMix(slugMinutes, appointmentTypes, totalSeenMin);
  const appointmentGroupMix = computeAppointmentGroupMix(appointmentMix);

  return {
    dvmDays,
    dvmHours,
    avgHoursPerDay,
    avgHoursPerWeekTotal,
    avgHoursPerDvmPerWeek,
    completeWeeksWorked,
    distinctDvms,
    distinctDates,
    dvmsPerDay,
    daysPerDvmPerWeek,
    totalScheduledMin,
    totalWorkedMin,
    workedHours,
    avgWorkedHoursPerDay,
    totalBlockoffMin,
    totalSeenMin,
    totalOtherTypesMin,
    totalAvailMin,
    blockoffRate,
    petSeenRate,
    otherTypesRate,
    availableTimeRate,
    multiBookedPct,
    totalCompleted,
    avgCompletedPerDay,
    totalCanceled,
    avgBookingLeadTimeDays,
    appointmentMix,
    appointmentGroupMix,
    avgSeenHoursPerDvmPerDay,
    avgOtherTypesHoursPerDvmPerDay,
    avgAvailHoursPerDvmPerDay,
    avgBlockoffHoursPerDvmPerDay,
    avgDvmDaysPerWeek,
  };
}

// rows must already have base filter + scope + date range applied
function sumCancelReasons(rows: SchedulingRow[]): Record<CancelReason, number> {
  const out = emptyCancelReasons();
  for (const r of rows) {
    for (const reason of CANCEL_REASONS) {
      out[reason] +=
        Number((r as unknown as Record<string, unknown>)[`canceled_appointments_${reason}`]) || 0;
    }
  }
  return out;
}

export function computeMetrics(
  rows: SchedulingRow[],
  periodStart: string,
  periodEnd: string,
  appointmentTypes: string[] = []
): ComputedMetrics {
  const totals: MetricTotals = {
    // One doctor on one date = one DVM-day, deduped so a doctor split across
    // two sites the same day (two scheduling_fact rows) isn't counted twice —
    // the same convention useTrendData and useRevenueTrendData use.
    dvmDays: new Set(rows.map((r) => `${r.resource_id}|${r.schedule_date}`)).size,
    distinctDvms: new Set(rows.map((r) => r.resource_id)).size,
    dvmWeeks: new Set(rows.map((r) => `${r.resource_id}|${getWeekKey(r.schedule_date)}`)).size,
    distinctDates: new Set(rows.map((r) => r.schedule_date)).size,
    totalScheduledMin: rows.reduce((s, r) => s + Number(r.scheduled_minutes), 0),
    totalWorkedMin: rows.reduce((s, r) => s + Number(r.total_worked_minutes), 0),
    totalBlockoffMin: rows.reduce((s, r) => s + Number(r.blockoff_minutes), 0),
    allSeenMin: rows.reduce((s, r) => s + Number(r.completed_appointment_minutes), 0),
    totalAvailMin: rows.reduce((s, r) => s + Number(r.available_minutes), 0),
    slugMinutes: sumKindMinutes(rows),
    totalBookableBlocks: rows.reduce((s, r) => s + Number(r.bookable_blocks), 0),
    totalMultiBookedBlocks: rows.reduce((s, r) => s + Number(r.multi_booked_blocks), 0),
    totalCompleted: rows.reduce((s, r) => s + Number(r.completed_appointments), 0),
    totalCanceled: rows.reduce((s, r) => s + Number(r.canceled_appointments), 0),
    totalMedRev: rows.reduce((s, r) => s + (Number(r.medical_net_sales_amount) || 0), 0),
    canceledByReason: sumCancelReasons(rows),
    totalCancelLeadDays: rows.reduce(
      (s, r) => s + (Number(r.canceled_appointment_lead_time_days) || 0),
      0
    ),
  };
  return deriveMetrics(totals, periodStart, periodEnd, appointmentTypes);
}
